import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect, lazy, Suspense } from 'react'
import { supabase } from './services/supabase'
import errorTracker from './services/errorTracking'
import reloadDebugger from './utils/reloadDebugger'
import { OrganizationProvider } from './contexts/OrganizationContext'
import { MasterAdminProvider } from './contexts/MasterAdminContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { ToastProvider } from './contexts/ToastContext'
import { LanguageProvider } from './contexts/LanguageContext'
import Layout from './components/Layout'
import LoadingSpinner from './components/LoadingSpinner'
import ProtectedRoute from './components/ProtectedRoute'
import MasterAdminProtectedRoute from './components/MasterAdminProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import SessionSecurityProvider from './components/SessionSecurityProvider'
import NavigationTracker from './components/NavigationTracker'

const AuthPage = lazy(() => import('./pages/AuthPage'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Clients = lazy(() => import('./pages/Clients'))
const ClientDetails = lazy(() => import('./pages/ClientDetails'))
const Examination = lazy(() => import('./pages/Examination'))
const CarePlans = lazy(() => import('./pages/CarePlans'))
const CarePlanDetails = lazy(() => import('./pages/CarePlanDetails'))
const Therapies = lazy(() => import('./pages/Therapies'))
const Sessions = lazy(() => import('./pages/Sessions'))
const OrganizationSettings = lazy(() => import('./pages/OrganizationSettings'))
const InviteTeamMember = lazy(() => import('./pages/InviteTeamMember'))
const Invitations = lazy(() => import('./pages/Invitations'))
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'))
const OnboardingAPI = lazy(() => import('./pages/OnboardingAPI'))
const UserSetup = lazy(() => import('./pages/UserSetup'))
const SecuritySettings = lazy(() => import('./pages/SecuritySettings'))
const BackupRestore = lazy(() => import('./pages/BackupRestore'))
const GDPRManagement = lazy(() => import('./pages/GDPRManagement'))
const ComplianceDashboard = lazy(() => import('./pages/ComplianceDashboard'))
const ErrorDashboard = lazy(() => import('./pages/ErrorDashboard'))
const MasterAdminDashboard = lazy(() => import('./pages/MasterAdminDashboard'))
const MasterAdminOrganizations = lazy(() => import('./pages/MasterAdminOrganizations'))
const MasterAdminOrganizationDetails = lazy(() => import('./pages/MasterAdminOrganizationDetails'))
const MasterAdminStorageLimits = lazy(() => import('./components/MasterAdminStorageLimits'))
const MasterAdminFeatureManagement = lazy(() => import('./pages/MasterAdminFeatureManagement'))
const MasterAdminOrganizationRequests = lazy(() => import('./pages/MasterAdminOrganizationRequests'))
const RevenueAnalytics = lazy(() => import('./pages/RevenueAnalytics'))
const ReportBuilder = lazy(() => import('./components/ReportBuilder'))
const PracticePerformance = lazy(() => import('./pages/PracticePerformance'))
const ClientOutcomes = lazy(() => import('./pages/ClientOutcomes'))
const ClientFollowups = lazy(() => import('./pages/ClientFollowups'))
const BillingManagement = lazy(() => import('./pages/BillingManagement'))
const LegalDocuments = lazy(() => import('./pages/LegalDocuments'))
const Profile = lazy(() => import('./pages/Profile'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const Support = lazy(() => import('./pages/Support'))
const Forum = lazy(() => import('./pages/Forum'))
const WebsiteManagement = lazy(() => import('./pages/WebsiteManagement'))
const WebsiteEditor = lazy(() => import('./pages/WebsiteEditor'))

const LazyPage = ({ children }) => (
  <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
)

function App() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    window.errorTracker = errorTracker

    const handleGlobalError = (event) => {
      errorTracker.captureException(event.error || new Error(event.message), {
        type: 'unhandledError',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      })
    }

    const handleUnhandledRejection = (event) => {
      errorTracker.captureException(event.reason || new Error('Unhandled promise rejection'), {
        type: 'unhandledRejection',
        promise: true,
      })
    }

    window.addEventListener('error', handleGlobalError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setLoading(false)

      if (session?.user?.id) {
        sessionStorage.setItem('lastAuthSession', JSON.stringify({
          userId: session.user.id,
          timestamp: Date.now(),
          event: 'INITIAL_LOAD',
        }))
      }

      if (session?.user) {
        errorTracker.setUser({
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name,
        })
      }
    })

    let lastKnownUserId = null

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      reloadDebugger.trackAuthStateChange(event, session)

      const currentUserId = session?.user?.id
      const isDuplicateSignIn = event === 'SIGNED_IN' &&
        lastKnownUserId === currentUserId &&
        currentUserId !== null

      if (event !== 'TOKEN_REFRESHED' && !isDuplicateSignIn) {
        setUser(session?.user ?? null)
        lastKnownUserId = currentUserId

        if (session?.user) {
          errorTracker.setUser({
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata?.name,
          })
        } else {
          errorTracker.setUser(null)
          lastKnownUserId = null
        }
      } else if (isDuplicateSignIn) {
        console.debug('[Auth] Ignoring duplicate SIGNED_IN event for existing session')
      }
    })

    return () => {
      subscription.unsubscribe()
      window.removeEventListener('error', handleGlobalError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <ThemeProvider>
          <ToastProvider>
            <Router>
            <NavigationTracker />
            <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to="/dashboard" /> : <LazyPage><AuthPage /></LazyPage>}
        />
        <Route
          path="/register"
          element={user ? <Navigate to="/dashboard" /> : <LazyPage><AuthPage /></LazyPage>}
        />
        <Route
          path="/onboarding"
          element={user ? <Navigate to="/dashboard" /> : <LazyPage><OnboardingPage /></LazyPage>}
        />
        <Route
          path="/onboarding-api"
          element={<LazyPage><OnboardingAPI /></LazyPage>}
        />
        <Route
          path="/reset-password"
          element={<LazyPage><ResetPassword /></LazyPage>}
        />
        <Route
          path="/terms"
          element={<Navigate to="/legal/terms" replace />}
        />
        <Route
          path="/privacy"
          element={<Navigate to="/legal/privacy" replace />}
        />
        <Route
          path="/legal/:documentType"
          element={<LazyPage><LegalDocuments /></LazyPage>}
        />
        <Route
          path="/setup"
          element={
            user ? (
              <MasterAdminProvider>
                <OrganizationProvider>
                  <LazyPage><UserSetup /></LazyPage>
                </OrganizationProvider>
              </MasterAdminProvider>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
              <Route
                path="/*"
                element={
                  user ? (
                    <MasterAdminProvider>
                      <OrganizationProvider>
                        <SessionSecurityProvider>
                          <Layout>
                            <LazyPage>
                            <Routes>
                      <Route
                        path="/master-admin"
                        element={
                          <MasterAdminProtectedRoute>
                            <MasterAdminDashboard />
                          </MasterAdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/master-admin/organizations"
                        element={
                          <MasterAdminProtectedRoute>
                            <MasterAdminOrganizations />
                          </MasterAdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/master-admin/organizations/:id"
                        element={
                          <MasterAdminProtectedRoute>
                            <MasterAdminOrganizationDetails />
                          </MasterAdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/master-admin/storage-limits"
                        element={
                          <MasterAdminProtectedRoute>
                            <MasterAdminStorageLimits />
                          </MasterAdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/master-admin/features"
                        element={
                          <MasterAdminProtectedRoute>
                            <MasterAdminFeatureManagement />
                          </MasterAdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/master-admin/organization-requests"
                        element={
                          <MasterAdminProtectedRoute>
                            <MasterAdminOrganizationRequests />
                          </MasterAdminProtectedRoute>
                        }
                      />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route
                        path="/clients"
                        element={
                          <ProtectedRoute resource="clients" action="read">
                            <Clients />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/clients/:id"
                        element={
                          <ProtectedRoute resource="clients" action="read">
                            <ClientDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/examination"
                        element={
                          <ProtectedRoute resource="clients" action="write">
                            <Examination />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/care-plans"
                        element={
                          <ProtectedRoute resource="care_plans" action="read">
                            <CarePlans />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/care-plans/:id"
                        element={
                          <ProtectedRoute resource="care_plans" action="read">
                            <CarePlanDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/therapies"
                        element={
                          <ProtectedRoute resource="therapies" action="read">
                            <Therapies />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/sessions"
                        element={
                          <ProtectedRoute resource="sessions" action="read">
                            <Sessions />
                          </ProtectedRoute>
                        }
                      />
                    <Route
                      path="/websites"
                      element={
                        <ProtectedRoute requireAdmin>
                          <WebsiteManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/websites/:websiteId/edit"
                      element={
                        <ProtectedRoute requireAdmin>
                          <WebsiteEditor />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/organization"
                      element={
                        <ProtectedRoute requireAdmin>
                          <OrganizationSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/organization/invite"
                      element={
                        <ProtectedRoute requireAdmin>
                          <InviteTeamMember />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/invitations"
                      element={
                        <ProtectedRoute>
                          <Invitations />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/security"
                      element={
                        <MasterAdminProtectedRoute>
                          <SecuritySettings />
                        </MasterAdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/backup-restore"
                      element={
                        <ProtectedRoute requireAdmin>
                          <BackupRestore />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/gdpr"
                      element={
                        <MasterAdminProtectedRoute>
                          <GDPRManagement />
                        </MasterAdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/errors"
                      element={
                        <MasterAdminProtectedRoute>
                          <ErrorDashboard />
                        </MasterAdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/support"
                      element={
                        <ProtectedRoute>
                          <Support />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/forum"
                      element={
                        <ProtectedRoute>
                          <Forum />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/onboarding-api"
                      element={<OnboardingAPI />}
                    />
                    <Route
                      path="/compliance"
                      element={
                        <ProtectedRoute requireAdmin>
                          <ComplianceDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <ProtectedRoute>
                          <ReportBuilder />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/performance"
                      element={
                        <ProtectedRoute>
                          <PracticePerformance />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/outcomes"
                      element={
                        <ProtectedRoute>
                          <ClientOutcomes />
                        </ProtectedRoute>
                      }
                    />
                      <Route
                        path="/followups"
                        element={
                          <ProtectedRoute>
                            <ClientFollowups />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/billing"
                        element={
                          <ProtectedRoute>
                            <BillingManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/master-admin/revenue"
                        element={
                          <MasterAdminProtectedRoute>
                            <RevenueAnalytics />
                          </MasterAdminProtectedRoute>
                        }
                      />
                            <Route path="/app" element={<Navigate to="/dashboard" />} />
                            </Routes>
                            </LazyPage>
                        </Layout>
                        </SessionSecurityProvider>
                      </OrganizationProvider>
                    </MasterAdminProvider>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>
        </Router>
        </ToastProvider>
      </ThemeProvider>
      </LanguageProvider>
      </ErrorBoundary>
    )
  }

export default App
